import { Calendar, dateFnsLocalizer, dayjsLocalizer, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import startOfWeek from 'date-fns/startOfWeek'
import getDay from 'date-fns/getDay'
import fr from 'date-fns/locale/fr'
import 'moment/locale/fr'
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTransfert } from '../../data/slices/transfertSlice';
import { useNavigate } from 'react-router-dom';
import { output } from '../../settings/themeSettings';
import { Box, Typography, Paper, Container, Toolbar, AppBar, IconButton, Tooltip } from '@mui/material';
import { Refresh, CalendarToday, DirectionsCar } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const localizer = momentLocalizer(moment)

const messages = {
    allDay: 'journée',
    previous: 'précédent',
    next: 'suivant',
    today: 'aujourd\'hui',
    month: 'mois',
    week: 'semaine',
    day: 'jour',
    agenda: 'Agenda',
    date: 'date',
    time: 'heure',
    event: 'événement',
    showMore: total => `+ ${total} événement(s) supplémentaire(s)`
}

const TransfertCalendar = () => {
    const { transferts, transfertsFetchStatus } = useSelector((state) => state.transferts);
    const dispatch = useDispatch();
    const [events, setEvents] = useState([]);
    const navigateTo = useNavigate();

    useEffect(() => {
        dispatch(fetchTransfert());  
    }, []);

    useEffect(() => {
        output(transferts);
        const _ev = [];
        Array.isArray(transferts) && transferts.map((tr) => {
            // Créer une date avec l'heure spécifiée
            const transfertDate = moment(tr.date).format('YYYY-MM-DD');
            const [hours, minutes] = (tr.heure || '00:00').split(':');
            const startDateTime = new Date(`${transfertDate}T${hours}:${minutes}:00`);
            
            // Durée estimée du transfert (2 heures par défaut)
            const endDateTime = new Date(startDateTime.getTime() + (2 * 60 * 60 * 1000));
            
            _ev.push({ 
                start: startDateTime,
                end: endDateTime,
                title: `${tr.lieuDepart} → ${tr.lieuArrivee}`,
                subtitle: `Heure: ${tr.heure || 'Non défini'}`,
                resource: {
                    ...tr,
                    type: 'transfert',
                    color: tr.type === 'aeroport' ? '#1976d2' : '#388e3c'
                }
            });
        });
        setEvents(_ev);
    }, [transferts]);

    const handleRefresh = () => {
        dispatch(fetchTransfert());
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            {/* Header */}
            <AppBar position="static" elevation={2} sx={{ bgcolor: 'primary.main' }}>
                <Toolbar>
                    <DirectionsCar sx={{ mr: 2 }} />
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        Calendrier des Transferts
                    </Typography>
                    <Tooltip title="Actualiser">
                        <IconButton color="inherit" onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Paper 
                    elevation={3} 
                    sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        '& .rbc-calendar': {
                            borderRadius: 1,
                            overflow: 'hidden'
                        }
                    }}
                >
                    {/* Calendar Header Info */}
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {events.length} transfert(s) programmé(s)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, bgcolor: '#1976d2', borderRadius: '50%' }} />
                                <Typography variant="caption">Aéroport</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, bgcolor: '#388e3c', borderRadius: '50%' }} />
                                <Typography variant="caption">Standard</Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Calendar Component */}
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: 600 }}
                        messages={messages}
                        views={['month', 'week', 'day', 'agenda']}
                        defaultView="week"
                        onSelectEvent={(e) => {
                            output(e);
                            (e.resource.circuit?.type && e.resource.circuit?.id) && 
                            navigateTo(`/dossiers/${e.resource.circuit.type}/${e.resource.circuit.id}`);
                        }}
                        eventPropGetter={(event) => ({
                            style: {
                                backgroundColor: event.resource.color || '#2196f3',
                                borderRadius: '4px',
                                border: 'none',
                                color: 'white',
                                padding: '2px 4px',
                                fontSize: '0.75rem',
                                fontWeight: 500
                            }
                        })}
                        tooltipAccessor={(event) => (
                            <Box>
                                <Typography variant="subtitle2">{event.title}</Typography>
                                <Typography variant="body2">
                                    {moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}
                                </Typography>
                                {event.subtitle && (
                                    <Typography variant="caption">{event.subtitle}</Typography>
                                )}
                            </Box>
                        )}
                    />
                </Paper>
            </Container>
        </Box>
    );
};

export default TransfertCalendar;
